import { PolicyShortfallRaise } from "../components/PolicyShortfalls";
import { FindingAlsoCheck } from "../components/FindingCrossChecks";
import { runAiJob, pollAiJob, rememberAiJob, forgetAiJob, recalledAiJobs } from "../lib/aiJob";
import { useEffect, useState, useCallback } from "react";
import { EvidenceRequestAnswerForm, EvidenceRequestAnswerSummary, type AnswerDocument, type SuggestedEvidenceDocument } from "../components/EvidenceRequestAnswer";
import { useSearchParams } from "react-router-dom";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { useDeepLinkScroll } from "../hooks/useDeepLinkScroll";
import { theme, card, countTag, emptyFrame, btnPrimary } from "../theme";
import { IndexTabs, StatusTag, type StatusTone } from "../components/Drawing";
import { LoadingBlock } from "../components/Spinner";
import { api, apiErrorMessage } from "../api/client";
import { controlRefWithStandard } from "../lib/controlRef";
import { useExperience } from "../context/ExperienceContext";
import { controlRoute } from "../utils/controlRoute";
import { useCurrentUser } from "../auth/useCurrentUser";
import { FindingRiskLink, type LinkedRisk } from "../components/FindingRiskLink";
import { FindingCorrectiveAction, type FindingActionSummary } from "../components/FindingCorrectiveAction";
import { RelatedRecords, type RelatedLink } from "../components/RelatedRecords";
import { KIND_HREFS } from "../components/EntityTags";

import { cbPalette, cbSafe } from "../lib/colourBlind";
interface FindingCommentItem {
  id: string;
  text: string;
  authorRole: string;
  createdAt: string;
  author: { displayName: string | null; email: string };
}

function FindingCommentThread({ findingId }: { findingId: string }) {
  const [comments, setComments] = useState<FindingCommentItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [postError, setPostError] = useState("");

  const load = useCallback(() => {
    api.get<{ comments: FindingCommentItem[] }>(`/findings/${findingId}/comments`)
      .then((d) => { setComments(d.comments); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [findingId]);

  useEffect(() => { load(); }, [load]);

  const handlePost = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const c = await api.post<FindingCommentItem>(`/findings/${findingId}/comments`, { text: draft.trim() });
      setComments((prev) => [...prev, c]);
      setDraft("");
    } catch { setPostError("Failed to post comment. Please try again."); } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 12, marginTop: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
        Discussion
        {comments.length > 0 && (
          <span style={{ ...countTag, display: "inline-block", marginLeft: 6 }}>
            {comments.length}
          </span>
        )}
      </div>
      {comments.map((c) => (
        <div key={c.id} style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "baseline", marginBottom: 2 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 3,
              background: c.authorRole === "auditor" ? "#8b5cf622" : theme.color.accentBg,
              color: c.authorRole === "auditor" ? "#8b5cf6" : theme.color.accentText,
            }}>
              {c.authorRole === "auditor" ? "Auditor" : "You"}
            </span>
            <span style={{ fontSize: 10, color: theme.color.faint }}>
              {new Date(c.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            </span>
          </div>
          <div style={{ fontSize: 12, color: theme.color.text, paddingLeft: 4, whiteSpace: "pre-wrap" }}>{c.text}</div>
        </div>
      ))}
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <input
          type="text"
          placeholder="Reply to auditor…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handlePost(); } }}
          style={{ flex: 1, background: theme.color.bg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "6px 10px", fontSize: 12, color: theme.color.text, outline: "none" }}
        />
        <button
          onClick={handlePost}
          disabled={saving || !draft.trim()}
          style={{ ...btnPrimary, fontSize: 11, padding: "6px 12px", opacity: saving || !draft.trim() ? 0.5 : 1 }}
        >
          {saving ? "…" : "Send"}
        </button>
      </div>
      {postError && <div style={{ fontSize: 11, color: "var(--c-dangerText)", marginTop: 4 }}>{postError}</div>}
    </div>
  );
}

interface AuditorFinding {
  id: string;
  bureauId: string;
  bureau?: { name: string } | null;
  controlId: string;
  frameworkId?: string | null;
  text: string;
  status: string;               // the auditor's verdict: open | acknowledged | resolved (approved)
  clientStatus: string | null;  // our own progress: acknowledged | resolved (awaiting verification)
  clientStatusAt: string | null;
  linkedRisk?: LinkedRisk | null;
  correctiveAction?: FindingActionSummary | null;
  closureProposed?: boolean;
  correctiveActionId?: string | null;
  evidenceRequests?: Array<{ id: string; status: string; message: string }>;
  createdAt: string;
}

/** A finding's own links, for its Related records panel. */
function findingLinks(f: AuditorFinding): RelatedLink[] {
  return [
    { kindLabel: "Control", label: controlRefWithStandard(f.controlId) ?? f.controlId, href: controlRoute(f.controlId, f.frameworkId ?? null) },
    ...(f.linkedRisk ? [{ kindLabel: "Risk", label: `${f.linkedRisk.displayId != null ? `RISK-${String(f.linkedRisk.displayId).padStart(3, "0")} ` : ""}${f.linkedRisk.title}`, href: KIND_HREFS.risk(f.linkedRisk.id) }] : []),
    ...(f.correctiveActionId ? [{ kindLabel: "Corrective action", label: "The action raised to fix it", href: KIND_HREFS.action(f.correctiveActionId) }] : []),
    ...(f.evidenceRequests ?? []).map((r) => ({ kindLabel: "Evidence request", label: `${r.message.length > 60 ? `${r.message.slice(0, 60)}…` : r.message} (${r.status})`, href: `/auditor-feedback?request=${encodeURIComponent(r.id)}` })),
  ];
}

// What the finding looks like from the client's side: the auditor's approval
// wins; otherwise our own progress; otherwise it is open.
type ClientView = "open" | "acknowledged" | "awaiting" | "approved";
function clientView(f: AuditorFinding): ClientView {
  if (f.status === "resolved") return "approved";
  if (f.clientStatus === "resolved") return "awaiting";
  if (f.clientStatus === "acknowledged") return "acknowledged";
  return "open";
}

interface ClauseReview {
  controlId: string;
  controlName: string;
  frameworkId: string | null;
  status: string | null;
  notes: string | null;
  updatedAt: string;
}

interface EvidenceRequest {
  id: string;
  controlId: string;
  frameworkId: string | null;
  message: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  bureau: { name: string };
  /** The newest version of each document already filed under the control (one-click answer). */
  suggestedDocuments?: SuggestedEvidenceDocument[];
  /** What answered it, once fulfilled (backend lib/evidenceRequests.ts). */
  responseNote?: string | null;
  fulfilledAt?: string | null;
  documents?: AnswerDocument[];
  finding?: { id: string; text: string; status: string } | null;
}

const REVIEW_STATUS_CONFIG: Record<string, { color: string; bg: string; label: string; tone: StatusTone }> = cbPalette({
  approved:    { color: "#22c55e", bg: "#22c55e22", label: "Approved", tone: "met" },
  needs_work:  { color: "#ef4444", bg: "#ef444422", label: "Needs Work", tone: "gap" },
  observation: { color: "#f59e0b", bg: "#f59e0b22", label: "Observation", tone: "partial" },
});

interface AiState {
  loading: boolean;
  result: string | null;
  error: string | null;
  visible: boolean;
}

const STATUS_CONFIG: Record<ClientView, { color: string; bg: string; label: string; tone: StatusTone }> = cbPalette({
  open:         { color: "#f59e0b", bg: "#f59e0b22", label: "Open", tone: "gap" },
  acknowledged: { color: "#3b82f6", bg: "#3b82f622", label: "Acknowledged", tone: "partial" },
  awaiting:     { color: "#8b5cf6", bg: "#8b5cf622", label: "Resolved — awaiting auditor verification", tone: "partial" },
  approved:     { color: "#22c55e", bg: "#22c55e22", label: "Approved by auditor", tone: "met" },
});

// The auditor's verdict on an unapproved finding, shown next to our own state.
const VERDICT_LABEL: Record<string, string> = { open: "Needs work", acknowledged: "Observation" };

const STATUS_FILTERS: Array<{ value: "all" | ClientView; label: string }> = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "awaiting", label: "Awaiting verification" },
  { value: "approved", label: "Approved" },
];

function renderRecommendation(text: string) {
  // Split on numbered sections like "1. READING OF THE FINDING"
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} style={{ height: 8 }} />;
    // Heading patterns: "1. SECTION TITLE" or "## heading"
    const isNumberedHeading = /^\d+\.\s+[A-Z]/.test(trimmed);
    const isMarkdownHeading = trimmed.startsWith("##");
    if (isNumberedHeading || isMarkdownHeading) {
      return (
        <div
          key={i}
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: theme.color.heading,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginTop: 14,
            marginBottom: 4,
          }}
        >
          {trimmed.replace(/^#+\s*/, "")}
        </div>
      );
    }
    // Bullet points
    if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
      return (
        <div key={i} style={{ display: "flex", gap: 6, marginBottom: 3 }}>
          <span style={{ color: theme.color.accent, flexShrink: 0 }}>•</span>
          <span style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.6 }}>
            {trimmed.replace(/^[-•]\s*/, "")}
          </span>
        </div>
      );
    }
    return (
      <div key={i} style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.7, marginBottom: 2 }}>
        {trimmed}
      </div>
    );
  });
}

export function AuditorFeedbackPage() {
  const navigate = usePortalNavigate();
  const { novice } = useExperience();
  const { user } = useCurrentUser();
  // Escalating to a risk is PATCH /findings/:id/link-risk, behind controls:write.
  const canLinkRisk = !!user?.permissions?.includes("controls:write");
  const [findings, setFindings] = useState<AuditorFinding[] | null>(null);
  const [clauseReviews, setClauseReviews] = useState<ClauseReview[]>([]);
  const [evidenceRequests, setEvidenceRequests] = useState<EvidenceRequest[]>([]);
  const [updatingRequest, setUpdatingRequest] = useState<Record<string, boolean>>({});
  // ?finding=<id> / ?request=<id>: My Actions links to one finding or one
  // evidence request; the page opens on its tab and brings it into view.
  const [searchParams] = useSearchParams();
  const findingParam = searchParams.get("finding");
  const requestParam = searchParams.get("request");
  const [activeTab, setActiveTab] = useState<"findings" | "consultant" | "evidence-requests">(requestParam ? "evidence-requests" : "findings");
  const [statusFilter, setStatusFilter] = useState("all");
  useDeepLinkScroll(activeTab === "evidence-requests" ? requestParam : findingParam, !!findings);
  const [aiStates, setAiStates] = useState<Record<string, AiState>>({});
  const [updatingStatus, setUpdatingStatus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    api.get<{ findings: AuditorFinding[] }>("/findings").then((d) => setFindings(d.findings));
    api.get<{ reviews: ClauseReview[] }>("/controls/clause-reviews").then((d) => setClauseReviews(d.reviews ?? [])).catch(() => {});
    api.get<{ requests: EvidenceRequest[] }>("/findings/evidence-requests").then((d) => setEvidenceRequests(d.requests ?? [])).catch(() => {});
  }, []);

  const recStatusPath = (jobId: string) => `/findings/ai-recommendation-jobs/${encodeURIComponent(jobId)}`;

  // The recommendation is written on the worker; this waits for the job, which carries on if the
  // page is left (and is picked up again below).
  async function followRecommendation(findingId: string, wait: Promise<{ recommendation: string }>) {
    setAiStates((prev) => ({
      ...prev,
      [findingId]: { loading: true, result: null, error: null, visible: true },
    }));
    try {
      const res = await wait;
      setAiStates((prev) => ({
        ...prev,
        [findingId]: { loading: false, result: res.recommendation, error: null, visible: true },
      }));
    } catch (e) {
      setAiStates((prev) => ({
        ...prev,
        [findingId]: {
          loading: false,
          result: null,
          error: apiErrorMessage(e, "Failed to generate recommendation. Please try again."),
          visible: true,
        },
      }));
    } finally {
      forgetAiJob(`finding-rec:${findingId}`);
    }
  }

  function runAiRecommendation(findingId: string) {
    return followRecommendation(findingId, runAiJob<{ recommendation: string }>(
      `/findings/${findingId}/ai-recommendation`, recStatusPath, {},
      { onJobId: (jobId) => rememberAiJob(`finding-rec:${findingId}`, jobId) },
    ));
  }

  // Picks up recommendations started before the page was left.
  useEffect(() => {
    for (const saved of recalledAiJobs("finding-rec:")) {
      void followRecommendation(saved.key.slice("finding-rec:".length), pollAiJob<{ recommendation: string }>(saved.jobId, recStatusPath, { waitMs: saved.remainingMs }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only ever moves our own clientStatus — the auditor's verdict is theirs to change.
  async function updateStatus(findingId: string, status: "acknowledged" | "resolved") {
    setUpdatingStatus((prev) => ({ ...prev, [findingId]: true }));
    try {
      const updated = await api.patch<{ id: string; status: string; clientStatus: string | null; clientStatusAt: string | null }>(`/findings/${findingId}/status`, { status });
      setFindings((prev) => prev
        ? prev.map((f) => f.id === findingId ? { ...f, clientStatus: updated.clientStatus, clientStatusAt: updated.clientStatusAt } : f)
        : prev
      );
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [findingId]: false }));
    }
  }

  const [answeringId, setAnsweringId] = useState<string | null>(null);
  async function updateRequestStatus(reqId: string, status: "acknowledged") {
    setUpdatingRequest((prev) => ({ ...prev, [reqId]: true }));
    try {
      await api.patch(`/findings/evidence-requests/${reqId}`, { status });
      setEvidenceRequests((prev) => prev.map((r) => r.id === reqId ? { ...r, status } : r));
    } finally {
      setUpdatingRequest((prev) => ({ ...prev, [reqId]: false }));
    }
  }

  function toggleVisible(findingId: string) {
    setAiStates((prev) => {
      const current = prev[findingId];
      if (!current) return prev;
      return { ...prev, [findingId]: { ...current, visible: !current.visible } };
    });
  }

  if (!findings) {
    return <LoadingBlock />;
  }

  const visible = statusFilter === "all" ? findings : findings.filter((f) => clientView(f) === statusFilter);

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Feedback &amp; Reviews
      </h1>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: theme.color.subtle }}>
        Findings from your auditor and control-level review notes from your consultant.
      </p>

      <IndexTabs
        items={[
          { id: "findings", label: "Auditor Findings", count: findings.length },
          { id: "consultant", label: "Consultant Notes", count: clauseReviews.length > 0 ? clauseReviews.length : undefined },
          { id: "evidence-requests", label: "Evidence Requests", count: evidenceRequests.filter((r) => r.status !== "fulfilled").length || undefined },
        ]}
        value={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: 20 }}
      />

      {activeTab === "consultant" && (
        <div>
          {clauseReviews.length === 0 ? (
            <div style={{ ...emptyFrame, padding: "48px 20px", color: theme.color.faint, fontSize: 13 }}>
              No consultant review notes yet. Your consultant will annotate controls here when they review your compliance evidence.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {clauseReviews.map((r) => {
                const cfg = REVIEW_STATUS_CONFIG[r.status ?? ""] ?? { color: theme.color.muted, bg: theme.color.panelAlt, label: r.status ?? "Pending", tone: "none" as const };
                return (
                  <div key={r.controlId} style={{ ...card, borderLeft: `3px solid ${cfg.color}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                      <button
                        onClick={() => navigate(controlRoute(r.controlId, r.frameworkId))}
                        style={{
                          fontVariantNumeric: "tabular-nums", fontSize: 10, padding: "2px 6px",
                          background: theme.color.accentBg, color: theme.color.accentText,
                          borderRadius: 3, border: `1px solid ${theme.color.border}`, cursor: "pointer",
                        }}
                      >
                        {controlRefWithStandard(r.controlId) ?? r.controlId}
                      </button>
                      <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, flex: 1 }}>
                        {r.controlName}
                      </span>
                      {r.status && (
                        <StatusTag tone={cfg.tone}>{cfg.label}</StatusTag>
                      )}
                    </div>
                    {r.notes && (
                      <div style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.7, marginBottom: 6 }}>
                        {r.notes}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: theme.color.faint }}>
                      Updated {new Date(r.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === "evidence-requests" && (
        <div>
          {evidenceRequests.length === 0 ? (
            <div style={{ ...emptyFrame, padding: "48px 20px", color: theme.color.faint, fontSize: 13 }}>
              No evidence requests from your auditor yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {evidenceRequests.map((r) => {
                const isPending = r.status === "pending";
                const isAck = r.status === "acknowledged";
                const isFulfilled = r.status === "fulfilled";
                const borderColor = isFulfilled ? cbSafe("#22c55e") : isPending ? "#f59e0b" : "#3b82f6";
                return (
                  <div key={r.id} data-deeplink={r.id} style={{ ...card, borderLeft: `3px solid ${borderColor}` }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                      <button
                        onClick={() => navigate(controlRoute(r.controlId, r.frameworkId))}
                        style={{
                          fontVariantNumeric: "tabular-nums", fontSize: 10, padding: "2px 6px",
                          background: theme.color.accentBg, color: theme.color.accentText,
                          borderRadius: 3, border: `1px solid ${theme.color.border}`, cursor: "pointer", flexShrink: 0,
                        }}
                      >
                        {controlRefWithStandard(r.controlId) ?? r.controlId}
                      </button>
                      <div style={{ flex: 1, fontSize: 13, color: theme.color.text, lineHeight: 1.6 }}>
                        {r.message}
                      </div>
                      <StatusTag tone={isFulfilled ? "met" : isAck ? "partial" : "none"}>
                        {isFulfilled ? "Fulfilled" : isAck ? "Acknowledged" : "Pending"}
                      </StatusTag>
                    </div>
                    <div style={{ fontSize: 11, color: theme.color.faint, marginBottom: 8 }}>
                      From {r.bureau.name} · {new Date(r.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      {r.finding && <> · for the finding “{r.finding.text.length > 80 ? `${r.finding.text.slice(0, 80)}…` : r.finding.text}”</>}
                    </div>
                    {isFulfilled && <EvidenceRequestAnswerSummary documents={r.documents} note={r.responseNote} fulfilledAt={r.fulfilledAt} />}
                    {!isFulfilled && answeringId === r.id && (
                      <EvidenceRequestAnswerForm<EvidenceRequest>
                        requestId={r.id}
                        suggestions={r.suggestedDocuments ?? []}
                        onCancel={() => setAnsweringId(null)}
                        onAnswered={(updated) => { setAnsweringId(null); setEvidenceRequests((prev) => prev.map((x) => x.id === r.id ? { ...x, ...updated, bureau: x.bureau, finding: x.finding } : x)); }}
                      />
                    )}
                    {!isFulfilled && answeringId !== r.id && (
                      <div style={{ display: "flex", gap: 8 }}>
                        {isPending && (
                          <button
                            onClick={() => updateRequestStatus(r.id, "acknowledged")}
                            disabled={updatingRequest[r.id]}
                            style={{ ...btnPrimary, fontSize: 12, padding: "5px 12px", opacity: updatingRequest[r.id] ? 0.6 : 1 }}
                          >
                            Acknowledge
                          </button>
                        )}
                        <button
                          onClick={() => setAnsweringId(r.id)}
                          disabled={updatingRequest[r.id]}
                          style={{
                            fontSize: 12, padding: "5px 12px", borderRadius: 3, cursor: "pointer",
                            border: `1px solid ${theme.color.success}`, background: theme.color.successBg, color: theme.color.successText, fontWeight: 600,
                            opacity: updatingRequest[r.id] ? 0.6 : 1,
                          }}
                        >
                          Answer with evidence
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === "findings" && novice && (
        <div style={{ ...card, marginBottom: 16, background: theme.color.accentBg, border: `1px solid ${theme.color.accent}` }}>
          <div style={{ fontSize: 12, color: theme.color.accentText, lineHeight: 1.7 }}>
            <strong>What is this?</strong> Your auditor has left observations about your compliance. <em>Open</em> means action is needed, <em>Acknowledged</em> means you've seen it, and <em>Resolved</em> means you've addressed it — your auditor then verifies and marks it <em>Approved</em>, or sends it back. Use the <strong>Get AI recommendations</strong> button on any finding to get practical advice on how to respond.
          </div>
        </div>
      )}

      {activeTab === "findings" && <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {STATUS_FILTERS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setStatusFilter(opt.value)}
            style={{
              padding: "8px 14px",
              borderRadius: 3,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              border: `1px solid ${
                statusFilter === opt.value ? theme.color.accent : theme.color.borderStrong
              }`,
              background: statusFilter === opt.value ? theme.color.accentBg : theme.color.panel,
              color: statusFilter === opt.value ? theme.color.accentText : theme.color.subtle,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>}

      {activeTab === "findings" && (visible.length === 0 ? (
        <div
          style={{
            ...emptyFrame,
            padding: "48px 20px",
            color: theme.color.faint,
            fontSize: 13,
          }}
        >
          {findings.length === 0
            ? "No auditor feedback yet. Your auditor will leave findings here after reviewing your evidence."
            : "No findings match this filter."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {visible.map((f) => {
            const view = clientView(f);
            const cfg = STATUS_CONFIG[view];
            const ai = aiStates[f.id];
            return (
              <div key={f.id} data-deeplink={f.id} style={card}>
                {/* Finding header */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                  <button
                    onClick={() => navigate(controlRoute(f.controlId, f.frameworkId))}
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 10,
                      padding: "2px 6px",
                      background: theme.color.accentBg,
                      color: theme.color.accentText,
                      borderRadius: 3,
                      border: `1px solid ${theme.color.border}`,
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  >
                    {controlRefWithStandard(f.controlId) ?? f.controlId}
                  </button>
                  <StatusTag tone={cfg.tone}>{cfg.label}</StatusTag>
                  {view !== "approved" && VERDICT_LABEL[f.status] && (
                    <span style={{ fontSize: 11, color: theme.color.faint, flexShrink: 0 }}>
                      Auditor: {VERDICT_LABEL[f.status]}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.7, marginBottom: 8 }}>
                  {f.text}
                  <FindingAlsoCheck findingKey={`auditor:${f.id}`} />
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 11, color: theme.color.faint, marginBottom: 12 }}>
                  <span>
                    {new Date(f.createdAt).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                  <span>
                    Bureau: {f.bureau?.name ?? "Certification bureau"}
                  </span>
                </div>

                {/* A finding that shows a policy fell short (docs/POLICY-ADOPTION.md "Lessons learned") */}
                <div style={{ marginBottom: 12 }}>
                  <PolicyShortfallRaise sourceKind="auditor_finding" sourceId={f.id} canRaise={canLinkRisk} />
                </div>

                {/* Client status actions — our own progress, verified by the auditor */}
                {view !== "approved" && (
                  <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
                    {view === "open" && (
                      <button
                        onClick={() => updateStatus(f.id, "acknowledged")}
                        disabled={!!updatingStatus[f.id]}
                        style={{
                          padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                          borderRadius: 3, border: "1px solid #3b82f6",
                          background: "#3b82f622", color: "#3b82f6",
                          opacity: updatingStatus[f.id] ? 0.6 : 1,
                        }}
                      >
                        ✓ Acknowledge
                      </button>
                    )}
                    {view !== "awaiting" ? (
                      <button
                        onClick={() => updateStatus(f.id, "resolved")}
                        disabled={!!updatingStatus[f.id]}
                        style={{
                          padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                          borderRadius: 3, border: cbSafe("1px solid #22c55e"),
                          background: cbSafe("#22c55e22"), color: cbSafe("#22c55e"),
                          opacity: updatingStatus[f.id] ? 0.6 : 1,
                        }}
                      >
                        ✓ Mark Resolved
                      </button>
                    ) : (
                      <>
                        <span style={{ fontSize: 11, color: theme.color.subtle }}>
                          Marked resolved{f.clientStatusAt ? ` on ${new Date(f.clientStatusAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""} — your auditor will verify it.
                        </span>
                        <button
                          onClick={() => updateStatus(f.id, "acknowledged")}
                          disabled={!!updatingStatus[f.id]}
                          style={{ background: "none", border: "none", fontSize: 11, color: theme.color.faint, cursor: "pointer", textDecoration: "underline", opacity: updatingStatus[f.id] ? 0.6 : 1 }}
                        >
                          Withdraw
                        </button>
                      </>
                    )}
                  </div>
                )}

                {(f.correctiveAction || (canLinkRisk && view !== "approved")) && (
                  <div style={{ marginBottom: 12 }}>
                    <FindingCorrectiveAction
                      findingId={f.id}
                      action={f.correctiveAction ?? null}
                      closureProposed={!!f.closureProposed}
                      canRaise={canLinkRisk && view !== "approved"}
                      onRaised={(action) => setFindings((prev) => prev?.map((x) => x.id === f.id ? { ...x, correctiveAction: action } : x) ?? prev)}
                    />
                  </div>
                )}

                {(f.linkedRisk || canLinkRisk) && (
                  <div style={{ marginBottom: 12 }}>
                    <FindingRiskLink
                      findingId={f.id}
                      linkedRisk={f.linkedRisk ?? null}
                      canEdit={canLinkRisk}
                      onChange={(risk) => setFindings((prev) => prev?.map((x) => x.id === f.id ? { ...x, linkedRisk: risk } : x) ?? prev)}
                      onOpenRisk={(id) => navigate(`/risk-register?risk=${id}`)}
                    />
                  </div>
                )}

                {/* AI recommendation controls */}
                <div style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 12 }}>
                  {!ai || (!ai.loading && !ai.result && !ai.error) ? (
                    <button
                      onClick={() => runAiRecommendation(f.id)}
                      style={{
                        ...btnPrimary,
                        background: theme.color.accentBg,
                        color: theme.color.accentText,
                        border: `1px solid ${theme.color.accent}`,
                        fontSize: 12,
                        padding: "7px 14px",
                      }}
                    >
                      Get AI recommendations
                    </button>
                  ) : (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        {ai.loading ? (
                          <span style={{ fontSize: 12, color: theme.color.subtle }}>
                            Generating recommendations…
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => runAiRecommendation(f.id)}
                              style={{
                                ...btnPrimary,
                                background: theme.color.accentBg,
                                color: theme.color.accentText,
                                border: `1px solid ${theme.color.accent}`,
                                fontSize: 12,
                                padding: "7px 14px",
                              }}
                            >
                              Regenerate
                            </button>
                            {ai.result && (
                              <button
                                onClick={() => toggleVisible(f.id)}
                                style={{
                                  border: `1px solid ${theme.color.borderStrong}`,
                                  background: "transparent",
                                  color: theme.color.muted,
                                  borderRadius: theme.radius.sm,
                                  padding: "7px 14px",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                              >
                                {ai.visible ? "Hide" : "Show"}
                              </button>
                            )}
                          </>
                        )}
                      </div>

                      {/* Error state */}
                      {ai.error && (
                        <div
                          style={{
                            fontSize: 12,
                            color: theme.color.dangerText,
                            background: cbSafe("#dc262622"),
                            border: cbSafe("1px solid #7f1d1d"),
                            borderRadius: 3,
                            padding: "8px 12px",
                          }}
                        >
                          {ai.error}
                        </div>
                      )}

                      {/* Result panel */}
                      {ai.result && ai.visible && !ai.loading && (
                        <div
                          style={{
                            borderLeft: `3px solid ${theme.color.borderStrong}`,
                            paddingLeft: 14,
                            marginTop: 4,
                          }}
                        >
                          {renderRecommendation(ai.result)}
                          <div
                            style={{
                              marginTop: 14,
                              fontSize: 10,
                              color: theme.color.faint,
                              fontStyle: "italic",
                            }}
                          >
                            AI-generated guidance — review before acting.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 10 }}>
                  <RelatedRecords entity={null} links={findingLinks(f)} defaultOpen={false} />
                </div>

                {/* Finding comment thread */}
                <FindingCommentThread findingId={f.id} />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
